import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  lazy,
  Suspense,
} from "react";

import {
  AddRounded,
  CloseOutlined,
  DeleteOutline,
  DownloadOutlined,
  EditOutlined,
  FeedOutlined,
  FilterAltOutlined,
  FilterAltRounded,
  KeyboardArrowDown,
  KeyboardArrowUp,
  KeyboardTab,
  RefreshOutlined,
  RestoreOutlined,
  SwapVertRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Divider,
  FormLabel,
  IconButton,
  Tooltip,
} from "@mui/material";

import { isArray, isNil, size } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import TextInput from "../../components/inputs/TextInput";
import SearchInput from "../../components/inputs/SearchInput";
import Flx from "../../components/layout/Flx";
import Screen from "../../components/layout/Screen";
import ScreenContent from "../../components/layout/ScreenContent";
import TitledHeader from "../../components/layout/TitledHeader";
import BasicModal from "../../components/modals/BasicModal";
import ToggleNavigation from "../../components/navigation/ToggleNavigation";
import Txt from "../../components/typography/Txt";
import { useLoanFilesHook } from "../../hooks/useLoanFilesHook";

import { blue, grey } from "@mui/material/colors";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { loanDrilldownClear } from "../../redux/actions/loanDrilldownActions";
import { loanPipelineSet } from "../../redux/actions/loanPipelineActions";
import LoadingFallback from "../../components/LoadingFallback";

// Lazy load heavy components
const AgButtonGetTableState = lazy(
  () => import("../../components/agGrid/AgButtonGetTableState")
);
const AgButtonSetTableState = lazy(
  () => import("../../components/agGrid/AgButtonSetTableState")
);
const UpdateLoanDataModal = lazy(
  () => import("../../components/common/UpdateLoanDataModal")
);
const LoanPipelineTable = lazy(() => import("./LoanPipelineTable"));

const ALL_TAB_LABEL_ACTIVE = "All Active";
const LOCAL_STORAGE_PIPELINE_KEY = "savedLoanPipelineFilter";

const LoanPipelineScreen = () => {
  const ref = useRef();
  const savedPipeline = localStorage.getItem(LOCAL_STORAGE_PIPELINE_KEY);
  const [quickFilter, setQuickFilter] = useState(null);
  const loanPipeline = useSelector((state) => state?.loanPipeline);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const { loading, getUserPipeline } = usePipelineHook();
  const dispatch = useDispatch();

  // Track if we're currently fetching to prevent duplicate requests
  const isFetchingRef = useRef(false);

  // Whether the table has been modified (i.e., show Reset/Save buttons)
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [isTableModified, setIsTableModified] = useState(false);
  const [pipeline, setPipeline] = useState(savedPipeline || "Underwriting");
  const [sortFilter, setSortFilter] = useState({
    colId: "updateDateTime",
    sort: "asc",
  });
  const [advancedFiltering, setAdvancedFiltering] = useState(false);
  const [loanStatusFilter, setLoanStatusFilter] =
    useState(ALL_TAB_LABEL_ACTIVE);

  const onPipelineStatusChange = (newPipeline) => {
    setPipeline(newPipeline);
    localStorage.setItem(LOCAL_STORAGE_PIPELINE_KEY, newPipeline);
  };

  /**
   * Fetch loan pipeline data - memoized to prevent recreation on every render
   */
  const fetchLoanPipeline = useCallback(() => {
    // Prevent multiple simultaneous calls
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    getUserPipeline({
      onSuccessFn: (data) => {
        dispatch(loanPipelineSet(data));
        isFetchingRef.current = false;
      },
      onErrorFn: (err) => {
        console.error("GetUserPipeline error:", err);
        isFetchingRef.current = false;
      },
    });
  }, [getUserPipeline, dispatch]);

  /**
   * Effect to fetch data when loanPipeline is null
   */
  useEffect(() => {
    if (isNil(loanPipeline) && !isFetchingRef.current) {
      fetchLoanPipeline();
    }
    if (!isNil(loanDrilldown)) {
      dispatch(loanDrilldownClear());
    }
  }, [loanPipeline, fetchLoanPipeline, loanDrilldown, dispatch]);

  const onStateUpdated = useCallback((params) => {
    // The 'gridInitializing' event means the table is just loading for the first time.
    if (isArray(params.sources) && params.sources[0] === "gridInitializing") {
      return;
    }
    setIsTableModified(true);
  }, []);

  /**
   * Clear all filters including external filters
   */
  const handleClearFilters = useCallback(() => {
    setLoanStatusFilter(ALL_TAB_LABEL_ACTIVE);
    setQuickFilter(null);
    // Reset the grid's internal filters via the ref
    if (ref.current && ref.current.api) {
      ref.current.api.setFilterModel(null);
    }
    setIsTableModified(false);
  }, []);

  const onLoanSelect = useCallback((data, field) => {
    setSelectedLoan({ loan: data, field });
  }, []);

  const onLoanSelectCancel = useCallback(() => {
    setSelectedLoan(null);
  }, []);

  const onSortFilterChange = useCallback(
    (sortState) => {
      const { colId, sort } = sortState;

      const sortValue =
        sortFilter?.colId === colId && sortFilter?.sort === sort ? null : sort;
      let finalSortState = { colId, sort: sortValue };

      if (ref.current && ref.current.api && ref.current.api.applyColumnState) {
        const columnState = [finalSortState];
        ref.current.api.applyColumnState({
          state: columnState,
          defaultState: { sort: null },
        });

        setSortFilter(finalSortState);
      }
    },
    [sortFilter]
  );

  return (
    <Screen>
      <TitledHeader
        hideBottomBorder
        title={"Loan Pipeline"}
        titleEndContent={
          <Tooltip title="Refresh loan data">
            <IconButton
              size="small"
              onClick={() => dispatch(loanPipelineSet(null))}
            >
              <RefreshOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        }
        quickFilter={quickFilter}
        sx={{ pb: 1 }}
        setQuickFilter={setQuickFilter}
        endContent={
          <Flx gap={1} jb ac>
            <Suspense fallback={<LoadingFallback />}>
              <AgCustomLayoutManager ref={ref} />
            </Suspense>
            <Divider orientation="vertical" flexItem sx={{ ml: 1, mr: 2 }} />
            <CreateLoanButton />
            <DownloadPipelineAsZipButton />
          </Flx>
        }
      />

      <Flx fw jb ac sx={{ px: 0.8, borderTop: `1px solid ${grey[300]}` }}>
        <PipelineSelector value={pipeline} onChange={onPipelineStatusChange} />
        <Flx gap={1} ac>
          {isTableModified && (
            <Button
              variant="outlined"
              startIcon={<RestoreOutlined className="thin-8" />}
              onClick={handleClearFilters}
              size="small"
              color="error"
            >
              Reset Table
            </Button>
          )}

          <Tooltip
            title={
              !advancedFiltering
                ? "Show Advanced Filtering"
                : "Hide Advanced Filtering"
            }
          >
            <IconButton
              color={advancedFiltering ? "primary" : "default"}
              onClick={() => setAdvancedFiltering((s) => !s)}
              size="small"
              sx={{
                background: advancedFiltering ? "#e3f2fd" : "inherit",
              }}
            >
              {advancedFiltering ? <FilterAltRounded /> : <FilterAltOutlined />}
            </IconButton>
          </Tooltip>
        </Flx>
      </Flx>
      <PrimaryContentArea>
        <SidebarPane
          loanStatusFilter={loanStatusFilter}
          setLoanStatusFilter={setLoanStatusFilter}
          quickFilter={quickFilter}
          pipelineFilter={pipeline}
          setQuickFilter={setQuickFilter}
          sortFilter={sortFilter}
          onSortFilterChange={onSortFilterChange}
        />
        <Suspense fallback={<LoadingFallback />}>
          <LoanPipelineTable
            ref={ref}
            rowData={loanPipeline}
            loading={loading}
            quickFilterText={quickFilter}
            onLoanSelect={onLoanSelect}
            onStateUpdated={onStateUpdated}
            pipelineFilter={pipeline}
            advancedFiltering={advancedFiltering}
            loanStatusFilter={loanStatusFilter}
          />

          <UpdateLoanDataModal
            loan={selectedLoan?.loan}
            field={selectedLoan?.field}
            onClose={onLoanSelectCancel}
          />
        </Suspense>
      </PrimaryContentArea>
    </Screen>
  );
};

/**
 * UI SECTIONS
 */

const LoanStatusSelector = ({ onChange, value, pipelineFilter }) => {
  const loanPipeline = useSelector((state) => state.loanPipeline);
  const filteredPipeline = useMemo(() => {
    if (isArray(loanPipeline)) {
      return loanPipeline.filter((loan) => loan?.pipeline === pipelineFilter);
    }
    return [];
  }, [loanPipeline, pipelineFilter]);

  const activeStatuses = useMemo(() => {
    const statusList = [
      ALL_TAB_LABEL_ACTIVE,
      "Prospect",
      "Terms Accepted",
      "Processing",
      "Appraisal Ordered",
      "Appraisal In",
      "Underwriting",
      "Final Review",
      "Clear To Close",
    ];
    if (isNil(filteredPipeline)) {
      return statusList;
    }
    const getLabel = (status) => {
      if (status === ALL_TAB_LABEL_ACTIVE) {
        const count = size(
          filteredPipeline?.filter(
            (loan) =>
              loan?.loanStatus !== "Dead" && loan?.loanStatus !== "Inactive"
          )
        );
        return `${status} (${count})`;
      }
      const count = size(
        filteredPipeline?.filter((loan) => loan?.loanStatus === status)
      );
      return `${status} (${count})`;
    };
    return statusList.map((status) => ({
      label: getLabel(status),
      value: status,
    }));
  }, [filteredPipeline]);

  const inactiveStatuses = useMemo(() => {
    const statusList = [
      "All Inactive",
      "Closed",
      "Balance Sheet",
      "Loan Sold",
      "Inactive",
      "Dead",
    ];
    if (isNil(filteredPipeline)) {
      return statusList;
    }
    const getLabel = (status) => {
      if (status === "All Inactive") {
        const count = size(
          filteredPipeline?.filter(
            (loan) =>
              loan?.loanStatus === "Dead" || loan?.loanStatus === "Inactive"
          )
        );
        return `${status} (${count})`;
      }
      const count = size(
        filteredPipeline?.filter((loan) => loan?.loanStatus === status)
      );
      return `${status} (${count})`;
    };
    return statusList.map((status) => ({
      label: getLabel(status),
      value: status,
    }));
  }, [filteredPipeline]);

  return (
    <SidebarFilterGroup
      icon={<FeedOutlined className="thin-9" />}
      label="Loans By Status"
    >
      <SidebarFilterItem
        label="In Progress Loans"
        value={value}
        tabs={activeStatuses}
        onChange={onChange}
      />

      <SidebarFilterItem
        label="Inactive Loans"
        value={value}
        tabs={inactiveStatuses}
        onChange={onChange}
        collapsible
        defaultCollapsed
      />
    </SidebarFilterGroup>
  );
};

const SortFilterSelector = ({ onSortFilterChange, value, allowDeselect }) => {
  const tabs = useMemo(
    () => [
      {
        label: "Recently Updated (Newest)",
        value: {
          colId: "updateDateTime",
          sort: "asc",
        },
      },
      {
        label: "Recently Updated (Oldest)",
        value: {
          colId: "updateDateTime",
          sort: "desc",
        },
      },
      {
        label: "Created Date (Newest)",
        value: {
          colId: "createDateTime",
          sort: "desc",
        },
      },
      {
        label: "Created Date (Oldest)",
        value: {
          colId: "createDateTime",
          sort: "asc",
        },
      },
    ],
    []
  );

  return (
    <Flx wrap column fw ac gap={2}>
      <SidebarFilterGroup icon={<SwapVertRounded />} label="Sort By">
        <SidebarFilterItem
          value={value}
          tabs={tabs}
          collapsible={false}
          allowDeselect={allowDeselect}
          onChange={onSortFilterChange}
        />
      </SidebarFilterGroup>
    </Flx>
  );
};

const SidebarPane = ({
  pipelineFilter,
  loanStatusFilter,
  setLoanStatusFilter,
  quickFilter,
  setQuickFilter,
  sortFilter,
  onSortFilterChange,
  width = "225px",
}) => {
  const [collapsed, setCollapsed] = useState(false);
  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1,
        background: "#fafafb",
        p: 1.8,
        px: collapsed ? 0 : 1.2,
        width: collapsed ? "30px" : width,
        borderTop: `1px solid ${grey[300]}`,
        borderRight: `1px solid ${grey[300]}`,
      }}
    >
      <Flx center fw gap={0.5} sx={{ mb: 3 }}>
        {collapsed ? null : (
          <SearchInput
            value={quickFilter}
            onChange={setQuickFilter}
            placeholder={"Search in table..."}
            sx={{
              ".MuiInputBase-root": {
                borderRadius: "50px !important",
                background: "#ffffff",
                fontSize: "11px",
                pl: 1.2,
                pr: 0.5,
                ".MuiSvgIcon-root": {
                  fontSize: "11px",
                },
              },
              input: {
                py: 1,
                height: "unset",
                borderRadius: "50px",
                fontSize: "11px",
              },
            }}
          />
        )}
        <Tooltip
          title={collapsed ? "Expand" : "Collapse"}
          placement="right"
          arrow
          disableInteractive
        >
          <IconButton onClick={() => setCollapsed((s) => !s)} size="small">
            <KeyboardTab
              sx={{
                fontSize: 12,
                transform: !collapsed ? "rotate(180deg)" : "rotate(0deg)",
                transition: "0.3s",
              }}
            />
          </IconButton>
        </Tooltip>
      </Flx>

      {collapsed ? null : (
        <Flx wrap column fw ac gap={2}>
          <LoanStatusSelector
            pipelineFilter={pipelineFilter}
            value={loanStatusFilter}
            onChange={setLoanStatusFilter}
          />
          <Divider flexItem />
          <SortFilterSelector
            value={sortFilter}
            onSortFilterChange={onSortFilterChange}
          />
        </Flx>
      )}
    </Box>
  );
};

const SidebarFilterGroup = ({ icon, label, children }) => {
  return (
    <Flx fw column gap={0}>
      <Flx
        ac
        gap={0.4}
        sx={{
          ml: 0.5,
          mb: 1,
          ".MuiSvgIcon-root": { fontSize: "14px !important" },
        }}
      >
        {icon}
        <Txt
          bold
          sx={{
            fontWeight: "500 !important",
            lineHeight: 1,
            fontSize: "12px",
          }}
        >
          {label}
        </Txt>
      </Flx>
      <Flx fw column gap={1}>
        {children}
      </Flx>
    </Flx>
  );
};

const SidebarFilterItem = ({
  label,
  value,
  tabs,
  onChange,
  allowDeselect,
  collapsible = true,
  defaultCollapsed = false,
}) => {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <Flx
      fw
      column
      gap={0}
      sx={{
        ".toggle-navigation-root": {
          gap: 0,
          ".Mui-selected": {
            backgroundColor: `${blue[50]} !important`,
            color: `#2962ff !important`,
          },
          ".MuiButtonBase-root": {
            fontSize: " 11px !important",
            m: 0,
            color: grey[600],
            py: "3px !important",
            px: "3px !important",
            ml: 0.6,
          },
        },
      }}
    >
      {label ? (
        <Button
          fullWidth
          sx={{
            px: 0,
            ml: 0.3,
            color: "#000000",
            justifyContent: "flex-start",
            fontSize: "11px",
            textTransform: "none",
            fontWeight: "400 !important",
            ".MuiButton-icon": {
              m: 0,
            },
          }}
          onClick={() => setCollapsed((s) => !s)}
          variant="text"
          color="inherit"
          startIcon={
            collapsed ? (
              <KeyboardArrowDown
                sx={{ color: "#212121 !important", fontSize: "18px" }}
                className="thin-8"
              />
            ) : (
              <KeyboardArrowUp
                sx={{ color: "#212121 !important", fontSize: "18px" }}
                className="thin-8"
              />
            )
          }
        >
          {label}
        </Button>
      ) : null}

      {collapsed ? null : (
        <ToggleNavigation
          tabs={tabs}
          sx={{
            ml: collapsible ? 1.4 : 0,
          }}
          value={value}
          allowDeselect={allowDeselect}
          orientation="vertical"
          underlinePosition="right"
          variant="underline"
          onChange={onChange}
        />
      )}
    </Flx>
  );
};

const PipelineSelector = ({ onChange, value }) => {
  const loanPipeline = useSelector((state) => state.loanPipeline);
  const pipelineTabs = useMemo(() => {
    const statusList = [
      "Borrower Portal Scenarios",
      "Scenario",
      "Underwriting",
      "Commercial",
    ];
    if (isNil(loanPipeline)) {
      return statusList;
    }
    const getLabel = (pipelineTab) => {
      const count = size(
        loanPipeline?.filter((loan) => loan?.pipeline === pipelineTab)
      );
      return `${pipelineTab} (${count})`;
    };
    return statusList.map((pipelineTab) => ({
      label: getLabel(pipelineTab),
      value: pipelineTab,
    }));
  }, [loanPipeline]);
  return (
    <ToggleNavigation tabs={pipelineTabs} value={value} onChange={onChange} />
  );
};

const AgCustomLayoutManager = forwardRef((props, ref) => {
  const lsLayoutKey = "loanPipelineLayouts";
  const [isEditing, setIsEditing] = useState(false);
  const [show, setShow] = useState(false);

  const storedLayouts = localStorage.getItem(lsLayoutKey);
  const [layouts, setLayouts] = useState(
    storedLayouts ? JSON.parse(storedLayouts) : []
  );

  const onLayoutSave = useCallback(
    (layout) => {
      const updatedLayouts = [...layouts, layout];
      setLayouts(updatedLayouts);
      localStorage.setItem(lsLayoutKey, JSON.stringify(updatedLayouts));
      setShow(false);
    },
    [layouts]
  );

  const onLayoutDelete = useCallback(
    (layoutName) => {
      const updatedLayouts = layouts.filter((l) => l.name !== layoutName);
      setLayouts(updatedLayouts);
      localStorage.setItem(
        "loanPipelineLayouts",
        JSON.stringify(updatedLayouts)
      );
    },
    [layouts]
  );

  return (
    <Flx
      gap={0.5}
      ac
      sx={{
        ".MuiSvgIcon-root": {
          fontSize: "16px !important",
        },
      }}
    >
      <Button
        startIcon={
          isNil(layouts) || layouts.length === 0 ? null : isEditing ? (
            <CloseOutlined className="thin" />
          ) : (
            <EditOutlined className="thin" />
          )
        }
        color="inherit"
        onClick={() => setIsEditing((s) => !s)}
        variant="text"
      >
        {isEditing ? "Done Editing" : "Custom Layouts:"}
      </Button>
      <Flx gap={0.2} ac>
        <SavedLayoutsRenderer
          ref={ref}
          layouts={layouts}
          onLayoutDelete={onLayoutDelete}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
        />
        <IconButton
          size="small"
          variant="outlined"
          startIcon={<AddRounded />}
          onClick={() => setShow(true)}
          ref={ref}
        >
          <AddRounded />
        </IconButton>
      </Flx>

      <LayoutDialog
        ref={ref}
        show={show}
        onSave={onLayoutSave}
        onCancel={() => setShow(false)}
      />
    </Flx>
  );
});

const LayoutDialog = forwardRef(({ show, onSave, onCancel }, ref) => {
  const [input, setInput] = useState(null);

  const handleOnSave = (fullState, detailedState) => {
    onSave({ name: input, initialState: detailedState });
    closeModal();
  };

  const closeModal = () => {
    setInput(null);
    onCancel();
  };

  return (
    <BasicModal
      maxWidth={"xs"}
      title={"Save Layout"}
      open={Boolean(show)}
      show={show}
      onClose={closeModal}
    >
      <FormLabel>Enter a name for your custom layout</FormLabel>
      <TextInput value={input} onChange={setInput} />

      <Flx sx={{ mt: 3 }} gap={1} end>
        <Suspense fallback={<LoadingFallback />}>
          <AgButtonGetTableState onClick={handleOnSave} ref={ref}>
            Save Layout
          </AgButtonGetTableState>
        </Suspense>
      </Flx>
    </BasicModal>
  );
});

const SavedLayoutsRenderer = forwardRef(
  ({ isEditing, setIsEditing, layouts, onLayoutDelete }, ref) => {
    const onDeleteClick = (layoutName) => {
      if (size(layouts) === 1) {
        setIsEditing(false);
      }
      onLayoutDelete(layoutName);
    };

    if (isNil(layouts) || layouts.length === 0) {
      return null;
    }

    return (
      <Flx gap={0.6} wrap ac>
        {layouts.map((layout, idx) => {
          if (isEditing) {
            return (
              <Button
                key={layout.name}
                color="error"
                onClick={() => onDeleteClick(layout.name)}
                variant="outlined"
                endIcon={<DeleteOutline sx={{ fontSize: "14px !important" }} />}
              >
                {layout.name}
              </Button>
            );
          }
          return (
            <Suspense key={idx} fallback={<LoadingFallback />}>
              <AgButtonSetTableState
                variant="outlined"
                gridState={layout.initialState}
                ref={ref}
              >
                {layout.name}
              </AgButtonSetTableState>
            </Suspense>
          );
        })}
      </Flx>
    );
  }
);

const CreateLoanButton = () => {
  const navigate = useNavigate();
  return (
    <Button
      startIcon={<AddRounded />}
      size="small"
      onClick={() => navigate("/loan/create")}
    >
      Create Loan
    </Button>
  );
};

const DownloadPipelineAsZipButton = () => {
  const { loading, downloadPipeline } = useLoanFilesHook();

  const onDownloadClick = () => {
    downloadPipeline({
      fileDownloadName: "Pipeline Files",
    });
  };

  return (
    <Tooltip placement="bottom-start" title="Download Pipeline as ZIP">
      <IconButton
        loading={loading}
        onClick={onDownloadClick}
        size="small"
        color="primary"
      >
        <DownloadOutlined className="thin-7" sx={{ fontSize: 24 }} />
      </IconButton>
    </Tooltip>
  );
};

/**
 * UI LAYOUT STATELESS
 */

const PrimaryContentArea = ({ children }) => {
  return (
    <ScreenContent
      sx={{
        p: 0,
        height: "100%",
        width: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "row",
        position: "relative",
      }}
    >
      {children}
    </ScreenContent>
  );
};

export default LoanPipelineScreen;
